// frontend/src/AccessibilityAudit/Checklist.jsx — "What passes: WCAG 2.1 AA checklist" (spec 2.1 item 6). The statuses
// are worked out on the server (report.checklist) for the chosen device; this only shows and filters them.
import React, { useState } from 'react';

const CK = {
  pass: { label: 'Passed automated checks', cls: 'st-pass' },
  fail: { label: 'Failed', cls: 'sev-critical' },
  review: { label: 'Needs review', cls: 'sev-moderate' },
  na: { label: 'Nothing to check on these pages', cls: 'st-na' },
  manual: { label: 'Not checked automatically', cls: 'st-manual' },
};
const ORDER = ['pass', 'fail', 'review', 'na', 'manual'];
const ICON = { pass: '✓', fail: '✗', review: '?', na: '–' };
const devWord = (d) => (d === 'phone' ? 'Phone' : 'Desktop');
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

function RuleLine({ r }) {
  const detail = Object.entries(r.devices).map(([d, x]) => (
    x.status === 'pass' ? `${devWord(d)}: ${plural(x.pages.length, 'page')}, ${plural(x.nodes, 'element')} checked`
      : x.status === 'fail' ? `${devWord(d)}: failed on ${plural(x.fail_pages.length, 'page')}`
        : x.status === 'review' ? `${devWord(d)}: needs review` : `${devWord(d)}: nothing to check`)).join(' · ');
  const note = r.kind === 'interaction' ? Object.values(r.devices).flatMap((x) => x.notes || [])[0] : null;
  return (
    <li className={`uh-ax-ckr ${r.status}`}>
      <span className="icon" aria-hidden="true">{ICON[r.status]}</span>
      <span>
        <span>{r.help}</span> {r.kind === 'interaction' ? <span className="uh-ax-chip ix">Interaction check</span> : <code>{r.id}</code>}
        <br /><small>{detail}</small>{note && <><br /><small>{note}</small></>}
      </span>
    </li>
  );
}

export default function Checklist({ data, device }) {
  const [filter, setFilter] = useState('pass');
  const [open, setOpen] = useState({});
  if (!data) return <section className="uh-card"><div className="uh-empty">Loading the checklist…</div></section>;
  const crits = data.criteria;
  const n = {}; ORDER.forEach((k) => { n[k] = crits.filter((c) => c.status === k).length; });
  const shown = crits.filter((c) => filter === 'all' || c.status === filter);
  const groups = [...new Set(shown.map((c) => c.principle))];
  return (
    <section className="uh-card" aria-labelledby="uh-ax-ck-title" style={{ paddingBottom: 8 }}>
      <div className="uh-ax-ck-h">
        <div>
          <h3 id="uh-ax-ck-title">What passes: WCAG 2.1 AA checklist</h3>
          <p>All 50 WCAG 2.1 A and AA criteria and what the scan could tell about each one, {device === 'both' ? 'on desktop and phone' : `on ${device}`}.</p>
        </div>
        <div className="uh-ax-ck-chips" role="group" aria-label="Show criteria">
          {ORDER.map((k) => <button type="button" key={k} className={`uh-ax-ck-chip ${CK[k].cls}`} aria-pressed={filter === k} onClick={() => setFilter(k)}><b>{n[k]}</b> {CK[k].label}</button>)}
          <button type="button" className="uh-ax-ck-chip all" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>All 50</button>
        </div>
      </div>
      <p className="uh-ax-intro"><b>Passed automated checks</b> means every axe test and interaction check for that criterion found no problems on the scanned pages.
        They test only part of most criteria, so this is a good sign, not proof of compliance. {n.manual} criteria have no automated test and need a person to check them.</p>
      {!shown.length && <div className="uh-empty">No criteria with this status.</div>}
      {groups.map((g) => (
        <div className="uh-ax-ck-g" key={g}>
          <h4>{g}</h4>
          {shown.filter((c) => c.principle === g).map((c) => {
            const isOpen = !!open[c.id];
            const nAxe = c.rules.filter((r) => r.kind !== 'interaction').length; const nIx = c.rules.length - nAxe;
            const how = c.status === 'manual' ? 'Manual only'
              : [nAxe ? plural(nAxe, 'axe test') : '', nIx ? plural(nIx, 'interaction check') : ''].filter(Boolean).join(' · ');
            return (
              <div className={`uh-ax-crit ${CK[c.status].cls}`} key={c.id}>
                <button type="button" className="uh-ax-crit-h" aria-expanded={isOpen} onClick={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))}>
                  <span className="uh-ax-chev" aria-hidden="true">›</span>
                  <span><b>{c.id}</b> {c.name} <span className="uh-ax-chip">{c.level}</span></span>
                  <span className="uh-ax-crit-how">{how}</span>
                  <span className="uh-ax-pill">{CK[c.status].label}</span>
                </button>
                {isOpen && (
                  <div className="uh-ax-crit-b">
                    {c.rules.length ? <ul>{c.rules.map((r) => <RuleLine key={r.id} r={r} />)}</ul>
                      : <p>Neither axe nor the interaction checks can test this. It needs a person to judge it, for example whether wording, media or reading order make sense.{' '}
                        <a href="https://www.w3.org/WAI/WCAG21/Understanding/" target="_blank" rel="noopener noreferrer">Understanding WCAG 2.1 ↗</a></p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </section>
  );
}
